import React, { createContext, useState, useEffect } from 'react';

// Create theme context
export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Get theme from localStorage or use default
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  
  // Apply theme when it changes
  useEffect(() => {
    // Update the data-theme attribute on the document element
    document.documentElement.setAttribute('data-theme', theme);
    
    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Set up battery monitoring for auto theme change
  useEffect(() => {
    const isPowerSavingEnabled = localStorage.getItem('power-saving') === 'true';
    if (!isPowerSavingEnabled || !('getBattery' in navigator)) return;
    
    const handleBatteryChange = (battery) => {
      if (battery.level <= 0.2) {
        // Battery is low, set power-saving attribute
        document.documentElement.setAttribute('data-power-saving', 'true');
        
        // If not already in dark mode, switch to it
        if (theme !== 'dark' && !battery.charging) {
          setTheme('dark');
        }
      } else if (battery.level > 0.2 || battery.charging) {
        // Battery is OK or charging, remove power-saving
        document.documentElement.removeAttribute('data-power-saving');
      }
    };
    
    let batteryObj = null;
    
    const setupBatteryMonitoring = async () => {
      try {
        batteryObj = await navigator.getBattery();
        
        // Initial check
        handleBatteryChange(batteryObj);
        
        // Add listeners
        batteryObj.addEventListener('levelchange', () => handleBatteryChange(batteryObj));
        batteryObj.addEventListener('chargingchange', () => handleBatteryChange(batteryObj));
      } catch (error) {
        console.error('Error setting up battery monitoring:', error);
      }
    };
    
    setupBatteryMonitoring();
    
    // Clean up listeners on unmount
    return () => {
      if (batteryObj) {
        batteryObj.removeEventListener('levelchange', () => handleBatteryChange(batteryObj));
        batteryObj.removeEventListener('chargingchange', () => handleBatteryChange(batteryObj));
      }
    };
  }, [theme]);
  
  // Apply system preference on first load
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (!savedTheme && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const prefersContrast = window.matchMedia('(prefers-contrast: more)').matches;
      
      if (prefersContrast) {
        setTheme('high-contrast');
      } else if (prefersDark) {
        setTheme('dark');
      }
    }
  }, []);
  
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};